// ZoomControls — the zoom panel; a HUD sibling of the verdict strip.

import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectScale,
  setScale,
  resetView,
  fitToView,
  MIN_SCALE,
  MAX_SCALE,
} from '../../store/slices/canvasSlice.ts';
import { useCanvasLayout } from './useCanvasLayout.ts';

const ZOOM_STEP = 0.25;

export interface ZoomControlsProps {
  /** Content size to fit; measured from the loaded layout when omitted. */
  contentW?: number;
  contentH?: number;
}

export default function ZoomControls(props: ZoomControlsProps) {
  const layout = useCanvasLayout();
  const contentW = props.contentW ?? layout.totalWidth;
  const contentH = props.contentH ?? layout.totalHeight;
  const dispatch = useDispatch();
  const scale = useSelector(selectScale);

  const zoomIn = useCallback(() => {
    dispatch(setScale(Math.min(MAX_SCALE, scale + ZOOM_STEP)));
  }, [dispatch, scale]);

  const zoomOut = useCallback(() => {
    dispatch(setScale(Math.max(MIN_SCALE, scale - ZOOM_STEP)));
  }, [dispatch, scale]);

  const handleReset = useCallback(() => {
    dispatch(resetView());
  }, [dispatch]);

  const handleFit = useCallback(() => {
    // Get the container dimensions from the parent .timeline-scroll element
    const container = document.querySelector('.timeline-scroll');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    dispatch(
      fitToView({
        contentW,
        contentH,
        containerW: rect.width,
        containerH: rect.height,
      }),
    );
  }, [dispatch, contentW, contentH]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;

      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        zoomIn();
      } else if (e.key === '-') {
        e.preventDefault();
        zoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        handleReset();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoomIn, zoomOut, handleReset]);

  const pct = Math.round(scale * 100);

  return (
    <div className="zoom-controls">
      <button
        className="zoom-btn"
        onClick={zoomOut}
        disabled={scale <= MIN_SCALE}
        title="Zoom out (Ctrl+-)"
        aria-label="Zoom out"
      >
        −
      </button>
      <button
        className="zoom-percentage"
        onClick={handleReset}
        title="Reset zoom (Ctrl+0)"
        aria-label={`Zoom ${pct}%, click to reset`}
      >
        {pct}%
      </button>
      <button
        className="zoom-btn"
        onClick={zoomIn}
        disabled={scale >= MAX_SCALE}
        title="Zoom in (Ctrl+=)"
        aria-label="Zoom in"
      >
        +
      </button>
      <button className="zoom-btn" onClick={handleFit} title="Fit to view" aria-label="Fit to view">
        ⊞
      </button>
    </div>
  );
}
